import type { KyInstance } from "ky";
import { toSearchParams } from "./pagination.js";
import type { PaginatedRequest } from "./pagination.js";
import type {
  IsoDateTime,
  ListPractitionersQuery,
  PractitionerEducationDto,
  PractitionerType,
  SessionFormat,
  TextData,
  WeeklyAvailability,
} from "./practitioners.js";
import type { TaxonomyOption } from "./taxonomy.js";
import { TURNSTILE_TOKEN_HEADER } from "./turnstile.js";

export interface PractitionerPublicProfileDto {
  username: string;

  firstName: string;
  lastName: string;
  title?: TaxonomyOption;

  profilePhotoUrl?: string;

  focusAreas: TaxonomyOption[];
  otherFocusAreas: TaxonomyOption[];
  therapeuticApproaches: TaxonomyOption[];
  culturalBackground: TaxonomyOption[];
  languages: TaxonomyOption[];
  ageGroups: TaxonomyOption[];
  clientTypes: TaxonomyOption[];
  fundingPathways: TaxonomyOption[];
  thirdPartySchemes: TaxonomyOption[];

  credentials: PractitionerPublicCredentialDto[];
  education: PractitionerEducationDto[];
  certifications: PractitionerPublicCertificationDto[];

  derivedYearsOfExperience: number;

  about?: TextData;
  /** The old site's bio as plain text. Show it in place of `about` while that is empty. */
  legacyAbout?: string;

  sessionFormats: SessionFormat[];
  practiceAddresses: PractitionerPublicAddressDto[];
  timezone?: string;
  availability: WeeklyAvailability;

  websiteUrl?: string;

  gender?: "male" | "female";
  isAcceptingNewClients: boolean;

  distanceMetres?: number;

  createdAt: IsoDateTime;
}

export interface PractitionerPublicListDto {
  items: PractitionerPublicProfileDto[];
  total: number;
  offset: number;
  limit: number;
}

/** Free-text search: the caller describes who they want, we work out the filters. */
export interface SearchPractitionersQuery {
  query: string;
}

/**
 * The slice of ListPractitionersQuery the search endpoint infers from free text.
 * Location and pagination are the caller's to set, and availability is not
 * inferred, so neither appears here.
 */
export type GeneratedFilters = Pick<
  ListPractitionersQuery,
  | "gender"
  | "practitionerType"
  | "sessionFormat"
  | "focusAreas"
  | "therapeuticApproaches"
  | "culturalBackground"
  | "languages"
  | "ageGroups"
  | "clientTypes"
  | "fundingPathways"
  | "thirdPartySchemes"
>;

/** What one model call consumed. The same shape for every LLM-backed endpoint. */
export interface LlmTokenUsage {
  inputTokens: number;
  outputTokens: number;
}

/** @deprecated Use {@link LlmTokenUsage} — kept so the search diagnostics name still reads. */
export type SearchTokenUsage = LlmTokenUsage;

/** What the interpretation cost us, for the debug panel on the search page. */
export interface SearchDiagnostics {
  model: string;
  durationMs: number;
  costUsd: number;
  usage: SearchTokenUsage;
}

/**
 * What the search endpoint made of a free-text query. It returns no practitioners:
 * the caller lists them with `list` and these `filters`, the same way the manual
 * controls do.
 */
export interface PractitionerSearchDto {
  /**
   * The filters we understood the query to mean, so the UI can show them back
   * to the user and list with them. Empty when `interpreted` is false.
   */
  filters: GeneratedFilters;
  /**
   * False when the query could not be interpreted — the model was unreachable
   * or answered with nonsense. There are then no filters to list with, so the
   * caller should say so rather than presenting the whole directory as matches.
   */
  interpreted: boolean;
  /**
   * Why the query could not be interpreted. Present only when `interpreted`
   * is false, so the UI can fall back to the manual filters and say why.
   */
  reason?: string;
  /** Model, timing and token cost. Absent when `interpreted` is false. */
  diagnostics?: SearchDiagnostics;
  /**
   * True when the query asked for a location — "near me", nearby, or a named
   * suburb or city — so the UI should trigger its near-me flow. Always false
   * when `interpreted` is false.
   */
  nearMe: boolean;
}

export interface PractitionerPublicCredentialDto {
  type: PractitionerType;
  registeredBody: TaxonomyOption;
  psychologyEndorsements: TaxonomyOption[];
  isVerified: boolean;
}

export interface PractitionerPublicCertificationDto {
  id: string;
  name: string;
  issuingBody?: string;
  yearQualified: IsoDateTime;
}

export interface PractitionerPublicAddressDto {
  id: string;
  countryCode: string;
  administrativeAreaLevel1?: string;
  administrativeAreaLevel2?: string;
  postcode?: string;
  locationName: string;
  formattedAddress?: string;
  lat?: number;
  lng?: number;
}

export class PractitionerPublicApi {
  constructor(private readonly http: KyInstance) { }

  list = (query: ListPractitionersQuery = {}): Promise<PractitionerPublicListDto> =>
    this.http
      .get("practitioners", { searchParams: toSearchParams({ ...query }) })
      .json<PractitionerPublicListDto>();

  search = (query: SearchPractitionersQuery, turnstileToken: string): Promise<PractitionerSearchDto> =>
    this.http
      .get("practitioners/search", {
        searchParams: toSearchParams({ ...query }),
        headers: { [TURNSTILE_TOKEN_HEADER]: turnstileToken },
      })
      .json<PractitionerSearchDto>();

  byUsername = (username: string): Promise<PractitionerPublicProfileDto> =>
    this.http
      .get(`practitioners/${encodeURIComponent(username)}`)
      .json<PractitionerPublicProfileDto>();
}
